import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo, faSignOutAlt, faPaperPlane, faList, faPlus, faSearch, faLightbulb, faClock, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import InfoModal from "./modal/modal";
import DatabaseOverviewModal from "./modal/DatabaseOverviewModal";
import ChatMenu from "../Menu/bar-menu";
import userIcon from './images/icono4.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE } from "../config/api";
import "./loader.scss";
import ChatPlus from "../New Menu/plus";
import { notifyError } from "./Error/errors";
import { ToastContainer } from "react-toastify";
import DatosTabla from './../Tabla/tabla';
import { useAuth } from '../../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import './scss/home.scss';
import PaginatedOptions from './PaginatedOptions';
import ConversationHistory from '../History/ConversationHistory';

function Home() {
  const _url = API_BASE;
  const { logout, isAuthenticated, initSession } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { email } = location.state || {};
  const { rol } = location.state || {};
  const { alias } = location.state || {};

  // initSession en un ref para ejecutar solo una vez (no en cada render)
  const sessionInitialized = useRef(false);
  if (!sessionInitialized.current) {
    initSession();
    sessionInitialized.current = true;
  }

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const chatBoxRef = useRef(null);
  const [userId, setUserId] = useState(email || "");
  const [role, setRole] = useState(rol || "");
  const [aliase, setAliase] = useState(alias || "");
  const inputRef = useRef(null);
  const [menuLNOption, setMenuLNOption] = useState("Todas las sucursales");
  const [menuPlusOption, setMenuPlusOption] = useState("Búsqueda");
  const [historyList, setHistoryList] = useState([]);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversationTitle, setConversationTitle] = useState(null);
  const inFlightCtrlRef = useRef(null);
  const inFlightRef = useRef(false);
  const isMountedRef = useRef(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Refs por mensaje para apuntar a SU propia tabla
  const tablaRefs = useRef({}); // { [msgIndex]: ref }

  const LNOptions = useMemo(() => ([
    "Todas las sucursales",
    "Córdoba",
    "Mendoza",
    "Palermo",
    "Rosario"
  ]), []);

  const plusOptions = useMemo(() => ([
    "Búsqueda",
    "Detalle"
  ]), []);

  // ================== Timer de inactividad ==================
  const INACTIVITY_TIMEOUT = 1800000;
  const inactivityTimerRef = useRef(null);

  // Funciones de progreso (compatibilidad)
  const startProgress = useCallback(() => { }, []);
  const completeProgress = useCallback(() => {
    setLoadingProgress(false);
  }, []);
  const abortProgress = useCallback(() => {
    setLoadingProgress(false);
  }, []);

  // Ref para currentConversationId (evita stale closures en auto-save)
  const currentConvIdRef = useRef(null);
  useEffect(() => { currentConvIdRef.current = currentConversationId; }, [currentConversationId]);

  const autoSaveConversation = useCallback(async (msgs, convId = null) => {
    if (!userId || msgs.length === 0) return;

    const sessionId = sessionStorage.getItem('sessionId');

    // Excluir datos pesados de tablas del guardado
    const lightMessages = msgs.map(msg => ({
      text: msg.text,
      sender: msg.sender,
      timestamp: msg.timestamp,
      question: msg.question || null,
      table: null,
      tables_ids: msg.tables_ids || null,
      msgIndex: msg.msgIndex,
      mode: msg.mode || null,
      suggestion_html: msg.suggestion_html || null,
    }));

    let title = conversationTitle;
    if (!title) {
      const firstUserMsg = msgs.find(m => m.sender === 'user');
      title = firstUserMsg ? firstUserMsg.text.substring(0, 100) : 'Nueva conversación';
    }

    const resolvedConvId = convId || currentConvIdRef.current;

    try {
      const url = _url + '/api/chat/history';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-APP-TOKEN': 'token',
          'X-ACCOUNT-ID': 'account-id',
        },
        body: JSON.stringify({
          userId,
          sessionId,
          title,
          messages: lightMessages,
          businessLine: menuLNOption,
          conversationId: resolvedConvId,
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.conversationId && !currentConvIdRef.current) {
          setCurrentConversationId(data.conversationId);
        }
      }
    } catch (error) {
      console.error('Error auto-saving conversation:', error);
    }
  }, [userId, _url, menuLNOption, conversationTitle]);

  const handleLoadConversation = useCallback((loadedMessages, convId, title) => {
    tablaRefs.current = {};
    setMessages(loadedMessages);
    setCurrentConversationId(convId);
    setConversationTitle(title);
  }, []);

  const handleNewConversation = useCallback(async () => {
    if (inFlightCtrlRef.current) inFlightCtrlRef.current.abort();

    setMessages([]);
    setInput("");
    setCurrentConversationId(null);
    setConversationTitle(null);
    tablaRefs.current = {};
    inputRef.current?.focus();

    const url = _url + "/api/chat/reset";
    const sessionId = sessionStorage.getItem('sessionId');
    const newSessionId = uuidv4();
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-APP-TOKEN': 'token', 'X-ACCOUNT-ID': 'account-id' },
        body: JSON.stringify({ userId, role, sessionId, newSessionId: "", delete: false })
      });
      sessionStorage.setItem('sessionId', newSessionId);
      localStorage.setItem('sessionId', newSessionId);
    } catch (error) {
      console.error('Error resetting session:', error);
    }
  }, [_url, userId, role]);

  // Cleanup en unmount: abortar requests pendientes
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (inFlightCtrlRef.current) {
        inFlightCtrlRef.current.abort();
        inFlightCtrlRef.current = null;
      }
    };
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (!loading && !loadingProgress) {
      inactivityTimerRef.current = setTimeout(async () => {
        try {
          const url = _url + "/api/chat/reset";
          const headers = {
            'Content-Type': 'application/json',
            'X-APP-TOKEN': 'token',
            'X-ACCOUNT-ID': 'account-id',
          };
          const sessionId = sessionStorage.getItem('sessionId');
          const body = { userId, role, sessionId, newSessionId: "", delete: false };
          await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
        } catch (error) {
          console.error('Error al cerrar sesión:', error);
        }

        localStorage.clear();
        sessionStorage.clear();
        logout();
        navigate('/');
      }, INACTIVITY_TIMEOUT);
    }
  }, [loading, loadingProgress, userId, role, logout, navigate, _url]);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  // Manejo de sesión / auth
  useEffect(() => {
    if (email) {
      localStorage.setItem('userEmail', email);
      inputRef.current?.focus();
    }
    if (!isAuthenticated) {
      // Guardar datos ANTES de limpiar
      const savedEmail = localStorage.getItem("userEmail");
      const savedRole = localStorage.getItem("role");
      const savedAlias = localStorage.getItem("alias");
      localStorage.clear();
      sessionStorage.clear();
      logout();
      navigate('/', { state: { email: savedEmail, rol: savedRole, alias: savedAlias } });
    } else {
      sessionStorage.setItem('sessionId', localStorage.getItem('sessionId'));
      setUserId(localStorage.getItem('userEmail'));
      setRole(localStorage.getItem('role'));
      setAliase(localStorage.getItem('alias'));
      inputRef.current?.focus();
    }
  }, [email, isAuthenticated, logout, navigate]);

  useEffect(() => {
    if (!loading && !loadingProgress) inputRef.current?.focus();
  }, [loading, loadingProgress]);

  // Timer de inactividad
  useEffect(() => {
    resetInactivityTimer();

    const handleUserActivity = () => {
      resetInactivityTimer();
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      clearInactivityTimer();
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [resetInactivityTimer, clearInactivityTimer]);

  // Pausar/reanudar timer según estado
  useEffect(() => {
    if (loading || loadingProgress) {
      clearInactivityTimer();
    } else {
      resetInactivityTimer();
    }
  }, [loading, loadingProgress, resetInactivityTimer, clearInactivityTimer]);

  // ==== Helpers de envoltura de <strong> con <a> ====

  const normalizeValue = (val) => {
    if (val == null) return "";
    let s = String(val).trim();
    let neg = false;
    if (/^\(.*\)$/.test(s)) {
      neg = true;
      s = s.slice(1, -1);
    }
    s = s
      .replace(/\s?(MXN|USD|mxn|usd)\s*$/i, "")
      .replace(/^-\s*\$\s?/, "-")
      .replace(/^\$\s?/, "")
      .replace(/\s/g, "")
      .replace(/%$/, "");
    s = s.replace(/,/g, "");
    if (neg) s = "-" + s;
    return s;
  };

  // FIX: Regex con flag `g` para que .exec() avance correctamente y no cause loop infinito
  const STRONG_NUMBER_RE = /\s*(?:MXN|USD)?\s*(?:\(\s*)?[-‑+]?\s*\$?\s*[-+]?\s*(?:\d{1,3}(?:[,.\s']\d{3})+(?:[.,]\d+)?|\d+(?:[.,]\d+)?)(?:\s*(?:MXN|USD|%))?\s*(?:\)\s*)?\s*%?\s*/gi;

  function makeStrongLink(raw, msgIndex) {
    const strong = document.createElement("strong");
    strong.className = "kc-strong";

    const wrapper = document.createElement("span");
    wrapper.style.cursor = "pointer";

    const a = document.createElement("a");
    a.href = "#";
    a.className = "kc-jump";
    a.setAttribute("data-kc-val", raw.trim());
    a.setAttribute("data-msg-idx", String(msgIndex));
    a.textContent = raw.trim();
    a.title = "Clic para ver en tabla";

    wrapper.appendChild(a);
    strong.appendChild(wrapper);
    return strong;
  }

  function isYearLike(raw) {
    const t = raw.replace(/[^\d\-–/]/g, "").trim();

    if (/^\d{4}$/.test(t)) {
      const y = Number(t);
      if (y >= 1900 && y <= 2099) return true;
    }

    const m = t.match(/^(\d{4})\s*[-–/]\s*(\d{2,4})$/);
    if (m) {
      const y1 = Number(m[1]);
      const y2 = Number(m[2].length === 2 ? (m[1].slice(0, 2) + m[2]) : m[2]);
      if (y1 >= 1900 && y1 <= 2099 && y2 >= 1900 && y2 <= 2099) return true;
    }

    if (/^\d{1,2}[-/]\d{4}$/.test(t)) {
      const [mm, yy] = t.split(/[-/]/).map(Number);
      if (yy >= 1900 && yy <= 2099 && mm >= 1 && mm <= 12) return true;
    }

    return false;
  }

  // Regex de validación (sin g, solo para .test())
  const STRONG_NUMBER_TEST_RE = /^\s*(?:MXN|USD)?\s*(?:\(\s*)?[-‑+]?\s*\$?\s*[-+]?\s*(?:\d{1,3}(?:[,.\s']\d{3})+(?:[.,]\d+)?|\d+(?:[.,]\d+)?)(?:\s*(?:MXN|USD|%))?\s*(?:\)\s*)?\s*%?\s*$/i;

  function shouldWrap(raw) {
    const hasMoneyCue = /[\$%]|(?:\b(?:MXN|USD)\b)/i.test(raw);
    const hasDecimalsOrThousands = /[.,]\d{1,2}\b/.test(raw) || /\d{1,3}(?:[,.\s']\d{3})+/.test(raw);

    if (isYearLike(raw) && !(hasMoneyCue || hasDecimalsOrThousands)) return false;

    return STRONG_NUMBER_TEST_RE.test(raw);
  }

  const moveWarningsToTop = (html) => {
    if (!html || typeof html !== "string") return html;

    const container = document.createElement("div");
    container.innerHTML = html;

    const blockquotes = Array.from(container.querySelectorAll("blockquote"));
    if (blockquotes.length > 0) {
      blockquotes.forEach(blockquote => {
        container.insertBefore(blockquote, container.firstChild);
      });
    }

    return container.innerHTML;
  };

  const applyProductNameBold = (html) => {
    if (!html || typeof html !== "string") return html;

    const regex = /(\d+\.)\s*([^?<>\n]+?)\?/g;
    return html.replace(regex, (match, number, productName) => {
      return `${number} <strong>${productName.trim()}</strong>`;
    });
  };

  const wrapNumbersWithLinks = (html, msgIndex) => {
    if (!html || typeof html !== "string") return html;
    // Limitar procesamiento para evitar bloqueo en respuestas muy grandes
    if (html.length > 50000) return html;

    const container = document.createElement("div");
    container.innerHTML = html;

    // 1) Actualiza <strong>/<b> existentes
    container.querySelectorAll("strong, b").forEach((el) => {
      if (el.querySelector("a.kc-jump")) return;
      const raw = (el.textContent || "").trim();
      if (raw && STRONG_NUMBER_TEST_RE.test(raw) && shouldWrap(raw)) {
        const strongLink = makeStrongLink(raw, msgIndex);
        el.replaceChildren(strongLink.firstChild);
        el.classList.add("kc-strong");
      }
    });

    // 2) Recorre NODOS DE TEXTO
    const skipSelector = "a, .kc-jump, code, pre, script, style";
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (parent.closest(skipSelector)) return NodeFilter.FILTER_REJECT;
          if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          return STRONG_NUMBER_TEST_RE.test(node.nodeValue)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        }
      }
    );

    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    // Limitar cantidad de nodos a procesar para evitar bloqueo
    const maxNodes = 200;
    textNodes.slice(0, maxNodes).forEach((node) => {
      const text = node.nodeValue;
      const frag = document.createDocumentFragment();
      let last = 0;

      // FIX: usar regex con flag g y resetear lastIndex
      STRONG_NUMBER_RE.lastIndex = 0;
      let m;
      let iterations = 0;
      const maxIterations = 500; // Safety limit

      while ((m = STRONG_NUMBER_RE.exec(text)) && iterations < maxIterations) {
        iterations++;
        const start = m.index;
        const end = STRONG_NUMBER_RE.lastIndex;

        // Protección contra loop infinito: si no avanza, forzar avance
        if (end === start) {
          STRONG_NUMBER_RE.lastIndex = start + 1;
          continue;
        }

        const before = text.slice(last, start);
        if (before) frag.appendChild(document.createTextNode(before));

        const raw = m[0];
        if (shouldWrap(raw)) {
          frag.appendChild(makeStrongLink(raw, msgIndex));
        } else {
          frag.appendChild(document.createTextNode(raw));
        }

        last = end;
      }
      const after = text.slice(last);
      if (after) frag.appendChild(document.createTextNode(after));

      if (frag.childNodes.length > 0) {
        node.parentNode.replaceChild(frag, node);
      }
    });

    return container.innerHTML;
  };

  const handleLogout = async () => {
    const url = _url + "/api/chat/reset";
    const headers = {
      'Content-Type': 'application/json',
      'X-APP-TOKEN': 'token',
      'X-ACCOUNT-ID': 'account-id',
    };
    const sessionId = sessionStorage.getItem('sessionId');
    const body = { userId, role, sessionId, newSessionId: "", delete: false };
    try {
      await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    } catch { /* noop */ }
    localStorage.clear();
    sessionStorage.clear();
    logout();
    navigate('/');
  };

  const splitSuggestionFromHTML = (html) => {
    if (!html || typeof html !== "string") return { mainHTML: html, suggestionHTML: "" };

    const container = document.createElement("div");
    container.innerHTML = html;

    const candidates = Array.from(container.querySelectorAll("p, div, li"));

    let foundNode = null;
    for (const node of candidates) {
      const text = (node.textContent || "").trim().toLowerCase();
      if (!text) continue;

      const norm = text
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ");

      if (norm.startsWith("sugerencia contextual:")) {
        foundNode = node;
        break;
      }
      const strong = node.querySelector("strong, b");
      if (strong) {
        const t = (strong.textContent || "").trim().toLowerCase();
        const tnorm = t
          .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
          .replace(/\s+/g, " ");
        if (tnorm.startsWith("sugerencia contextual:")) {
          foundNode = node;
          break;
        }
      }
    }

    let suggestionHTML = "";
    if (foundNode) {
      suggestionHTML = foundNode.innerHTML;
      suggestionHTML = suggestionHTML
        .replace(/¿\s*Hay algo (más|mas)\s+en lo que pueda ayudarte\?\s*/i, "")
        .replace(/<\/?strong>/gi, "")
        .replace(/Sugerencia\s*contextual\s*[:\-–—>]*\s*/i, "")
        .trim();
      foundNode.remove();
    }

    return { mainHTML: container.innerHTML, suggestionHTML };
  };

  const handleSend = async (e) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    inFlightCtrlRef.current = new AbortController();

    const val = inputRef.current?.value ?? "";
    if (!val.trim() && !e.target.value?.trim()) {
      inputRef.current?.focus();
      inFlightRef.current = false;
      inFlightCtrlRef.current = null;
      return;
    }
    const userText = val === "" ? e.target.value : val;

    setHistoryList((prev) => [...prev, userText]);

    const userMessage = { text: userText, sender: "user", timestamp: new Date().toLocaleTimeString() };

    // Mensaje de carga temporal del bot
    const loadingMessage = {
      text: `
        <style>
          @keyframes kp-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes kp-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
        </style>
        <div style="
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding: 20px;
          text-align: center;
        ">
          <div style="
            display: inline-block;
            padding: 16px 24px;
            background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
            border-radius: 12px;
            box-shadow: 0 2px 8px rgba(0,102,204,0.15);
          ">
            <div style="display: flex; align-items: center; gap: 12px;">
              <div style="
                width: 24px;
                height: 24px;
                border: 3px solid #e3f2fd;
                border-top: 3px solid #0066cc;
                border-radius: 50%;
                animation: kp-spin 1s linear infinite;
              "></div>
              <span style="
                color: #1565c0;
                font-size: 15px;
                font-weight: 500;
                animation: kp-pulse 1.5s ease-in-out infinite;
              ">
                Procesando tu consulta...
              </span>
            </div>
          </div>
        </div>
      `,
      sender: "IA-GEN",
      timestamp: new Date().toLocaleTimeString(),
      isLoading: true
    };

    const updatedMessages = [...messages, userMessage, loadingMessage];
    setMessages(updatedMessages);
    setInput("");
    setLoadingProgress(true);

    startProgress(menuPlusOption);

    const url = _url + '/api/chat/message';
    const sessionId = sessionStorage.getItem('sessionId');
    const headers = {
      'Content-Type': 'application/json',
      'X-APP-TOKEN': 'token',
      'X-ACCOUNT-ID': 'account-id',
    };

    const body = {
      message: userText,
      context: {},
      sessionId,
      userId,
      role: "role",
      businessLine: menuLNOption,
      actionType: menuPlusOption
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: inFlightCtrlRef.current.signal,
      });

      // FIX: consumir el body UNA sola vez, manejar errores correctamente
      if (!response.ok) {
        let errorTitle = "Error en la consulta";
        let human = "Requerimos que la consulta sea más específica. Por favor, ayúdanos a replantearla.";
        try {
          const data = await response.json();
          const detail = data?.detail;
          if (detail) {
            if (typeof detail === "object" && detail.message) {
              human = detail.message;
              if (detail.code) errorTitle = detail.code === "EmptyQuestion" ? "Consulta vacía" : "Error en la consulta";
            } else if (typeof detail === "string") {
              human = detail;
            }
          }
        } catch {
          // Si no se puede parsear JSON, usar mensajes por status
          switch (response.status) {
            case 400: errorTitle = "Solicitud incorrecta"; human = "No hay conexión con el servidor de IBM, por favor intenta más tarde."; break;
            case 401: errorTitle = "Sin autorización"; human = "Error de autenticación con el servidor de IBM, por favor verifica las credenciales."; break;
            case 404: errorTitle = "Sin resultados"; human = "La consulta es demasiado compleja o abarca un rango muy amplio. Intenta refinar tu búsqueda."; break;
            case 500: errorTitle = "Servicio no disponible"; human = "No hay conexión con el servicio del chat, por favor verifica la conexión."; break;
            default: errorTitle = "Error inesperado"; break;
          }
        }
        notifyError(errorTitle, human);
        abortProgress();
        setLoadingProgress(false);
        // Eliminar mensaje de loading en caso de error
        const messagesWithoutLoading = updatedMessages.filter(msg => !msg.isLoading);
        setMessages(messagesWithoutLoading);
        inputRef.current?.focus();
        return;
      }

      const data = await response.json();

      // Verificar que el componente sigue montado
      if (!isMountedRef.current) return;

      const botIndex = updatedMessages.length;

      // PROCESAMIENTO DE HTML con protección contra errores
      let processedHTML = data.message || "";
      try {
        processedHTML = moveWarningsToTop(processedHTML);
        processedHTML = applyProductNameBold(processedHTML);
        processedHTML = wrapNumbersWithLinks(processedHTML, botIndex);
      } catch (err) {
        console.error("Error processing HTML features:", err);
        processedHTML = data.message || "";
      }

      if (!tablaRefs.current[botIndex]) {
        tablaRefs.current[botIndex] = React.createRef();
      }

      const botMessage = {
        question: data.question,
        table: data?.data?.planning_analytics_data,
        tables_ids: data?.data?.extracted_json?.product,
        text: processedHTML,
        sender: "IA-GEN",
        timestamp: new Date().toLocaleTimeString(),
        msgIndex: botIndex,
        mode: menuPlusOption,
        suggestion_html: ""
      };

      if (isMountedRef.current) {
        // Reemplazar el mensaje de loading con la respuesta real
        // El mensaje de loading es el último en updatedMessages
        const messagesWithoutLoading = updatedMessages.filter(msg => !msg.isLoading);
        const allMessages = [...messagesWithoutLoading, botMessage];
        setMessages(allMessages);
        completeProgress(data?.processing_stages || []);
        // Auto-guardar conversación después de cada respuesta del bot
        autoSaveConversation(allMessages);
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        // Request fue cancelado intencionalmente, no mostrar error
        // Eliminar mensaje de loading
        const messagesWithoutLoading = updatedMessages.filter(msg => !msg.isLoading);
        setMessages(messagesWithoutLoading);
        return;
      }
      notifyError("Sin conexión", "No hay conexión con el servicio del chat, por favor verifica la conexión.");
      console.error('Error:', error);
      abortProgress();
      // Eliminar mensaje de loading en caso de error
      const messagesWithoutLoading = updatedMessages.filter(msg => !msg.isLoading);
      setMessages(messagesWithoutLoading);
    } finally {
      inFlightRef.current = false;
      inFlightCtrlRef.current = null;
      if (isMountedRef.current) {
        setLoadingProgress(false);
        inputRef.current?.focus();
      }
    }
  };

  const handleReload = async () => {
    if (inFlightCtrlRef.current) inFlightCtrlRef.current.abort();
    await Promise.resolve();

    inputRef.current?.focus();
    setLoading(true);
    setMessages([]);
    setInput("");
    tablaRefs.current = {};

    const url = _url + "/api/chat/reset";
    const headers = {
      'Content-Type': 'application/json',
      'X-APP-TOKEN': 'token',
      'X-ACCOUNT-ID': 'account-id',
    };
    const sessionId = sessionStorage.getItem('sessionId');
    const newSessionId = uuidv4();
    const body = { userId, role, sessionId, newSessionId: "", delete: false };

    try {
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!response.ok) {
        inputRef.current?.focus();
        setLoading(false);
        logout();
        throw new Error('Error en la petición');
      }
      sessionStorage.setItem('sessionId', newSessionId);
      localStorage.setItem('sessionId', newSessionId);

      // Guardar datos ANTES de navegar
      const savedEmail = localStorage.getItem("userEmail");
      const savedRole = localStorage.getItem("role");
      const savedAlias = localStorage.getItem("alias");
      navigate('/', { state: { email: savedEmail, rol: savedRole, alias: savedAlias } });
    } catch (error) {
      console.error('Error:', error);
      inputRef.current?.focus();
      setLoading(false); // FIX: era setLoading(true), dejaba la UI bloqueada
      localStorage.clear();
      sessionStorage.clear();
      logout();
    }
  };

  // Scroll al final en cada nuevo mensaje
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);

  const isChatEmpty = messages.length === 0;

  // Delegación de clicks
  const onChatClick = (e) => {
    const a = e.target.closest('a.kc-jump');
    if (!a) return;
    e.preventDefault();
    const val = a.getAttribute('data-kc-val');
    const msgIdxStr = a.getAttribute('data-msg-idx');
    if (!val || !msgIdxStr) return;

    const msgIdx = Number(msgIdxStr);
    const ref = tablaRefs.current[msgIdx];
    if (ref && ref.current && typeof ref.current.jumpToValue === "function") {
      const ok = ref.current.jumpToValue(val);
      if (!ok) {
        const fallback = normalizeValue(val);
        ref.current.jumpToValue(fallback);
      }
    }
  };

  return (
    <div className="chatbot-container">
      {/* Mobile Menu Toggle */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        aria-label="Toggle menu"
      >
        <FontAwesomeIcon icon={mobileSidebarOpen ? faTimes : faBars} />
      </button>

      {/* Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div
          className={`sidebar-overlay ${mobileSidebarOpen ? 'active' : ''}`}
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${mobileSidebarOpen ? 'mobile-open' : ''}`}>
        <img src="/logoF.png" alt="Logo" className="logo" />
        <div className="sidebar-icons">
          <InfoModal />
          <DatabaseOverviewModal />
          <button
            className="refresh-btn"
            onClick={() => {
              setHistoryPanelOpen(!historyPanelOpen);
              setMobileSidebarOpen(false);
            }}
            title="Historial de conversaciones"
          >
            <FontAwesomeIcon icon={faClock} />
          </button>
          <button
            className="refresh-btn"
            onClick={() => {
              handleReload();
              setMobileSidebarOpen(false);
            }}
          >
            <FontAwesomeIcon icon={faRedo} />
          </button>
        </div>
      </div>

      <div className="main-content">
        {isChatEmpty ? (
          <div className="chat-window">
            <img src="/logoF.png" alt="Logo" className="main-logo" />
            <h2>Bienvenido a Grupo de Ferreteria,<br />¿Cómo puedo ayudarte hoy?</h2>
          </div>
        ) : (
          <div className="chat-content" ref={chatBoxRef}>
            <h2>Bienvenido a Grupo de Ferreteria,<br />¿Cómo puedo ayudarte hoy?</h2>
            <div className="chat-box" onClick={onChatClick}>
              {messages.map((msg, index) => {
                const isBot = msg.sender === "IA-GEN";
                const mode = (msg.mode === "Default") ? "Búsqueda" : msg.mode;
                const modeIcon = (mode === "Detalle") ? faLightbulb : faSearch;

                return (
                  <div
                    key={index}
                    className={`message ${msg.sender}`}
                    style={{ position: 'relative' }}
                  >
                    {isBot && msg.mode && (
                      <span className="mode-badge" title={mode}>
                        <FontAwesomeIcon icon={modeIcon} />
                        {mode}
                      </span>
                    )}

                    <div className={`chat-text-${msg.sender}`}>
                      {isBot && msg.text && msg.text.includes('Opciones disponibles:') ? (
                        <PaginatedOptions htmlContent={msg.text} />
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                      )}
                    </div>
                    {msg.table && (
                      <div className="scrollable-table" id={`table-wrapper-${index}`}>
                        <DatosTabla
                          ref={(r) => {
                            const idx = msg.msgIndex !== undefined ? msg.msgIndex : index;
                            tablaRefs.current[idx] = { current: r };
                          }}
                          data={msg.table}
                          tableId={`msg-${index}`}
                        />
                      </div>
                    )}
                    {isBot && msg.suggestion_html && (
                      <div className="suggestion-box">
                        <strong>¿Hay algo más en lo que pueda ayudarte?</strong>{" "}
                        <span dangerouslySetInnerHTML={{ __html: msg.suggestion_html }} />
                      </div>
                    )}
                    <div className="timestamp">{msg.timestamp}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="chat-input">
          {!isChatEmpty && (
            <ChatPlus
              menuOptions={plusOptions}
              selectedOption={menuPlusOption}
              setSelectedOption={setMenuPlusOption}
              icon={faPlus}
              loading={loading}
              loadingProgress={loadingProgress}
            />
          )}

          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSend({ target: { value: inputRef.current.value } });
            }}
            placeholder="Escribe la consulta que desea"
            disabled={loading || loadingProgress}
          />
          <ChatMenu
            menuOptions={LNOptions}
            selectedOption={menuLNOption}
            setSelectedOption={setMenuLNOption}
            icon={faList}
            loading={loading}
            loadingProgress={loadingProgress}
          />
          <button className="send-btn" onClick={handleSend} disabled={loading || loadingProgress}>
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </div>
      </div>

      {/* User Menu */}
      <div className="user-menu">
        <img src={userIcon} alt="User Avatar" className="user-avatar" />
        <span className="user-name">{aliase}</span>
        <button className="settings-btn" onClick={handleLogout}>
          <FontAwesomeIcon icon={faSignOutAlt} />
        </button>
      </div>

      <ConversationHistory
        userId={userId}
        isOpen={historyPanelOpen}
        onToggle={() => setHistoryPanelOpen(!historyPanelOpen)}
        onLoadConversation={handleLoadConversation}
        onNewConversation={handleNewConversation}
        currentConversationId={currentConversationId}
      />

      <ToastContainer />
    </div>
  );
}

export default Home;
